/*
 * AnalyticsContent.tsx (AbstractECommerce)
 *
 * Copyright © 2020 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Timothy Fadayini, 2020
 *
 * @file AnalyticsContent.tsx
 * @author Timothy Fadayini
 * @copyright 2020 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import { Card } from 'primereact/card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { AnalyticsTable } from './AnalyticsTable';
import { TransactionsAnalyticsTable } from './TransactionsAnalyticsTable';
import { VisitorsAnalyticsTable } from './VisitorsAnalyticsTable';
import {
	ChartDashboardItem,
	NumericDashboardItem,
	CustomComponentDashboardItem
} from './DashboardItem';
import { MultiSelect } from 'primereact/multiselect';
import eventConstants from "src/Utils/EventConstants";

/**
 * Interface for AnalyticsContent properties.
 */
interface IAnalyticsContentProperties
{
	data: {
		perProduct: { event: { event: string, productName: string }, count: number }[];
		global: { event: string, count: number }[];
	},
}

/**
 * A utility function that groups an array by a specific property.
 *
 * @param array The array to be grouped by properties.
 * @param property The property to group the array by.
 * @return An object that includes the grouped array.
 */
function groupBy<T>(array: T[], property: keyof T | ((object: T) => string))
{
	return array.reduce(function (output: { [key: string]: T[] }, x: T)
	{
		const key = typeof property === 'function' ? property(x) : x[property] as any as string;
		if (output[key] == undefined)
		{
			output[key] = [];
		}

		output[key].push(x);
		return output;
	}, {});
}

const eventNames = {
	VIEW_PRODUCT: 'Products Viewed',
	VIEW_PRODUCT_LOGGED_IN: 'Products Viewed (logged-in)',
	INITIATE_PURCHASE: 'Purchases initiated',
	CHECKOUT_AND_PAYMENT: 'Attempted Payment',
	ADDED_DISCOUNT_CODE: 'Added discount code',
	REMOVED_DISCOUNT_CODE: 'Removed discount code',
	SUBSCRIBED_TO_NEWSLETTER: 'Subscribed to the newsletter',
	UNSUBSCRIBED_TO_NEWSLETTER: 'Unsubscribed from the newsletter',
	PURCHASE_SUCCESSFUL: 'Payment Successful',
	PURCHASE_UNSUCCESSFUL: 'Payment Failed',
	PURCHASE_HELP_REQUESTED: 'Payment Help Requested',
};

export const AnalyticsContent = (properties: IAnalyticsContentProperties) => {
	function remapName(name: string): string
	{
		if(name in eventNames)
			return (eventNames as any)[name];
		return name;
	}

	const globalItems = properties.data?.global?.map(entry => new NumericDashboardItem(remapName(entry.event), entry.count)) ?? [];
	const perProductItems = Object.entries(groupBy(properties.data?.perProduct ?? [], (entry) => entry.event.productName))
		.map(([product, entries]) => ({
			name: product,
			entries: entries.map(entry => new NumericDashboardItem(remapName(entry.event.event), entry.count, entry.event.productName))
		}));

	return (
		<>
			<Row><h2>Analytics</h2></Row>
			<Row>
				{globalItems.map((eachDashboard) => eachDashboard.getDashboard())}
			</Row>

			{
				perProductItems.map(entry => <>
					<Row><h2>Product: {entry.name}</h2></Row>
					<Row>
						{entry.entries.map((eachDashboard) => eachDashboard.getDashboard())}
					</Row>
				</>)
			}
		</>
	);
};
